import { throttle } from '@cian/newbuilding-utils';
import { useIntersectionObserver, useIsomorphicLayoutEffect } from '@cian/react-utils';

import { type FC, useCallback, useRef, useState } from 'react';

import { PikLogoContainer } from 'shared/components/PikLogo';
import { Callback } from 'shared/containers/Callback';
import { PikPhoneNumber } from 'shared/containers/PikPhoneNumber';
import { ECallbackPlace } from 'shared/types/callback';

import { Header } from './Header';
import { trackHeaderNavSectionClick } from './internal/tracking/trackHeaderNavSectionClick';
import { trackShowHeaderSection } from './internal/tracking/trackShowHeaderSection';
import { getPrevSiblingDistanceToTop } from './internal/utils/getPrevSiblingDistanceToTop';

export const HeaderContainer: FC = () => {
  const [isHeaderFixed, setHeaderFixed] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const place = isHeaderFixed ? ECallbackPlace.BlockStickyTop : ECallbackPlace.HeadBlock;

  const handleFixHeader = useCallback(() => {
    const header = headerRef.current;

    /* istanbul ignore next */
    if (!header) {
      return;
    }

    const headerHeight = header.getBoundingClientRect().height;
    const prevSiblingDistanceToTop = getPrevSiblingDistanceToTop(header);
    const windowYOffset = window.pageYOffset;

    if (windowYOffset > prevSiblingDistanceToTop + headerHeight) {
      setHeaderFixed(true);
    } else {
      setHeaderFixed(false);
    }
  }, []);

  useIsomorphicLayoutEffect(() => {
    const handleScroll = throttle(200, handleFixHeader);

    handleFixHeader();
    window.addEventListener('scroll', handleScroll);

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleFixHeader]);

  useIntersectionObserver({
    elementRef: headerRef,
    onInViewport: ({ subscribeState }) => {
      subscribeState.setSubscribeUnavailable();

      trackShowHeaderSection();
    },
  });

  return (
    <Header
      callback={<Callback place={place} size="small" />}
      isFixed={isHeaderFixed}
      logo={<PikLogoContainer />}
      number={<PikPhoneNumber />}
      ref={headerRef}
      onNavSectionClick={trackHeaderNavSectionClick}
    />
  );
};
